import { Link, useParams } from "react-router-dom";
import { Clock, BookOpen, Users, BarChart3, Check, ArrowLeft } from "lucide-react";
import { COURSES, INSTRUCTORS, PACKAGES, inr } from "@/data/content";
import { Reveal, Stars, CourseCard, useBuyPackage } from "@/components/site/ui";
import { useEnquiry } from "@/components/site/EnquiryDialog";

const NotFound = ({ to, label }) => (
  <div className="max-w-3xl mx-auto py-32 text-center"><h1 className="font-display text-5xl font-bold">Not found</h1><Link to={to} className="btn-neon mt-8">{label}</Link></div>
);

export default function CourseDetail() {
  const { slug } = useParams();
  const { open } = useEnquiry();
  const buy = useBuyPackage();
  const c = COURSES.find((x) => x.slug === slug);
  if (!c) return <NotFound to="/courses" label="Back to courses" />;
  const mentor = INSTRUCTORS.find((i) => i.name === c.instructor);
  const pkg = PACKAGES.find((p) => p.slug === c.package);

  return (
    <div data-testid="course-detail-page" className="max-w-7xl mx-auto px-5 sm:px-8 pt-10">
      <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--pink)]"><ArrowLeft size={16} /> All courses</Link>
      <div className="grid lg:grid-cols-12 gap-10 mt-6">
        <Reveal className="lg:col-span-7">
          <span className="eyebrow">{c.category} · {c.level}</span>
          <h1 data-testid="course-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 leading-[1.02]">{c.title}</h1>
          <p className="mt-4 text-[var(--muted)]">{c.short}</p>
          <div className="flex flex-wrap gap-5 mt-6 text-sm">
            <span className="flex items-center gap-2"><Clock size={16} className="text-[var(--pink)]" /> {c.duration}</span>
            <span className="flex items-center gap-2"><BookOpen size={16} className="text-[var(--pink)]" /> {c.lessons} lessons</span>
            <span className="flex items-center gap-2"><Users size={16} className="text-[var(--pink)]" /> {c.students.toLocaleString("en-IN")} learners</span>
            <span className="flex items-center gap-2"><BarChart3 size={16} className="text-[var(--pink)]" /> {c.level}</span>
            <span className="flex items-center gap-2"><Stars n={c.rating} /> {c.rating}</span>
          </div>
          <img src={c.image} alt={c.title} className="rounded-[2rem] w-full h-80 object-cover mt-8 border border-[var(--surface-border)]" />
          <h2 className="font-display text-3xl font-bold mt-10">What's included</h2>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {c.outcomes.map((o) => <div key={o} className="glass rounded-2xl p-4 flex gap-2 text-sm"><Check size={18} className="text-[var(--pink)] shrink-0" /> {o}</div>)}
          </div>
          <h2 className="font-display text-3xl font-bold mt-10">Course modules</h2>
          <ol className="mt-4 space-y-3">
            {c.modules.map((m, i) => (
              <li key={m} className="glass rounded-2xl px-5 py-4 flex items-center gap-4"><span className="font-display text-xl font-semibold neon-text w-8">{String(i + 1).padStart(2, "0")}</span>{m}</li>
            ))}
          </ol>
        </Reveal>
        <aside className="lg:col-span-5">
          <div className="glass rounded-[2rem] p-7 lg:sticky lg:top-28 shadow-[0_0_40px_-12px_var(--glow-pink)]">
            <p className="text-sm text-[var(--muted)]">Included in <Link to={`/packages/${pkg.slug}`} className="font-bold text-[var(--pink)]">{pkg.name}</Link> with {pkg.courses.length} courses</p>
            <p className="mt-1"><span data-testid="course-price" className="text-5xl font-bold neon-text">{inr(pkg.price)}</span><span className="line-through text-[var(--muted)] ml-3">{inr(pkg.oldPrice)}</span></p>
            <button data-testid="course-buy-btn" onClick={() => buy(pkg)} className="btn-neon w-full justify-center mt-6">Buy Now</button>
            <button data-testid="course-enquire-btn" onClick={() => open("contact", c.title)} className="btn-ghost w-full justify-center mt-3">Ask a Question</button>
            {mentor && (
              <div className="flex items-center gap-4 mt-8 pt-6 border-t border-[var(--surface-border)]">
                <img src={mentor.image} alt={mentor.name} className="w-16 h-16 rounded-full object-cover border-2 border-[var(--pink)]" />
                <div><p className="text-xs text-[var(--muted)]">Your mentor</p><p className="font-display text-lg font-semibold">{mentor.name}</p><p className="text-xs text-[var(--pink)]">{mentor.role}</p></div>
              </div>
            )}
          </div>
        </aside>
      </div>
      <h2 className="font-display text-4xl font-bold mt-24 mb-8">You may also <span className="neon-text">like</span></h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
        {COURSES.filter((x) => x.slug !== slug && x.package === c.package).slice(0, 3).map((x, i) => <CourseCard key={x.slug} c={x} i={i} />)}
      </div>
    </div>
  );
}

export { NotFound };
